import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateAdminInput } from "@/api/routes/admins/types";
import type {
	AddToCartInput,
	UpdateCartItemInput,
} from "@/api/routes/cart/types";
import type { CreateFavouriteInput } from "@/api/routes/favourites/types";
import type { CreateNotificationInput } from "@/api/routes/notifications/types";
import type { CreateCheckoutSessionInput } from "@/api/routes/payments/types";
import type {
	CreatePreferencesInput,
	UpdatePreferencesInput,
} from "@/api/routes/preferences/types";
import { authClient } from "../../context/auth-client";
import client from "../../utils/api-client";
import {
	useCreateAddressMutation,
	useDeleteAddressMutation,
	useGetAllAddressesQuery,
	useGetZonesQuery,
	useUpdateAddressMutation,
	useValidateAddressMutation,
} from "./use-addresses-api";
import {
	useCreateAllergyMutation,
	useCreateExtraMutation,
	useCreateMenuItemMutation,
	useCreateSizeMutation,
	useDeleteExtraMutation,
	useGetAllergiesQuery,
	useGetCategoriesQuery,
	useGetCategoryImagesQuery,
	useGetCategorySizesQuery,
	useGetExtrasQuery,
	useGetMenuItemByDisplayIdQuery,
	useGetMenuItemByIdQuery,
	useGetMenuItemsInfiniteQuery,
	useGetMenuItemsQuery,
	useGetSizesQuery,
	useSearchMenuItemsQuery,
	useUpdateExtraMutation,
	useUpdateMenuItemMutation,
} from "./use-menu-api";
import {
	useCancelOrderMutation,
	useCreateOrderMutation,
	useGetAllOrdersInfiniteQuery,
	useGetAllOrdersQuery,
	useGetOrderByIdQuery,
	useGetOrderStatusesQuery,
	useGetOrdersByUserQuery,
	useSearchOrdersQuery,
	useUpdateOrderStatusMutation,
} from "./use-orders-api";
import {
	useDeleteUserMutation,
	useGetAllUsersQuery,
	useGetCurrentUserProfileQuery,
	useGetUserByIdQuery,
	useUpdateUserMutation,
	useUpdateUserProfileMutation,
} from "./use-users-api";

const baseUrl =
	process.env.BUN_PUBLIC_SERVER_URL || "http://localhost:3000/api/v1";

export function useApi() {
	const queryClient = useQueryClient();

	return {
		// Auth
		auth: {
			useSession: authClient.useSession,
			signUp: authClient.signUp,
			signIn: authClient.signIn,
			signOut: authClient.signOut,
			getSession: authClient.getSession,
			updateUser: authClient.updateUser,
			changePassword: authClient.changePassword,
			requestPasswordReset: authClient.requestPasswordReset,
			resetPassword: authClient.resetPassword,
			verifyEmail: authClient.verifyEmail,
			sendVerificationEmail: authClient.sendVerificationEmail,
		},

		// Orders
		orders: {
			getAll: useGetAllOrdersQuery,
			getAllInfinite: useGetAllOrdersInfiniteQuery,
			getByUserId: useGetOrdersByUserQuery,
			getById: useGetOrderByIdQuery,
			getStatuses: useGetOrderStatusesQuery,
			searchByItem: useSearchOrdersQuery,
			create: useCreateOrderMutation(),
			updateStatus: useUpdateOrderStatusMutation(),
			cancel: useCancelOrderMutation(),
		},

		// Users
		users: {
			getAll: useGetAllUsersQuery,
			getById: useGetUserByIdQuery,
			getCurrentUserProfile: useGetCurrentUserProfileQuery,
			updateProfile: useUpdateUserProfileMutation(),
			update: useUpdateUserMutation(),
			delete: useDeleteUserMutation(),
			addresses: {
				getAll: useGetAllAddressesQuery,
				create: useCreateAddressMutation(),
				update: useUpdateAddressMutation(),
				delete: useDeleteAddressMutation(),
				getZones: useGetZonesQuery,
				validateAddress: useValidateAddressMutation(),
			},
		},

		// Menu
		menu: {
			getItemsInfinite: useGetMenuItemsInfiniteQuery,
			getItems: useGetMenuItemsQuery,
			getItemById: useGetMenuItemByIdQuery,
			getItemByDisplayId: useGetMenuItemByDisplayIdQuery,
			createItem: useCreateMenuItemMutation(),
			updateItem: useUpdateMenuItemMutation(),
			getCategories: useGetCategoriesQuery,
			getCategoryImages: useGetCategoryImagesQuery,
			searchItems: useSearchMenuItemsQuery,
			getSizes: useGetSizesQuery,
			getCategorySizes: useGetCategorySizesQuery,
			createSize: useCreateSizeMutation(),
			getAllergies: useGetAllergiesQuery,
			createAllergy: useCreateAllergyMutation(),
			getExtras: useGetExtrasQuery,
			createExtra: useCreateExtraMutation(),
			updateExtra: useUpdateExtraMutation(),
			deleteExtra: useDeleteExtraMutation(),
		},

		// Admins
		admins: {
			getAll: () =>
				useQuery({
					queryKey: ["admins"],
					queryFn: async () => {
						const result = await client.admins.index.$get();
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
			checkCurrentUserIsAdmin: (options?: { enabled?: boolean }) => {
				return useQuery({
					queryKey: ["admins", "check"],
					queryFn: async () => {
						const result = await client.admins.check.$get();
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
					enabled: options?.enabled !== false,
				});
			},
			create: useMutation({
				mutationFn: async (data: CreateAdminInput) => {
					const result = await client.admins.index.$post({ json: data });
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: () => {
					// Invalidate admins list since new admin was added
					queryClient.invalidateQueries({ queryKey: ["admins"] });
					console.log("Admin created successfully");
				},
				onError: () => console.log("Error: Failed to create admin"),
			}),
			delete: useMutation({
				mutationFn: async (id: number) => {
					const result = await client.admins[":id"].$delete({
						param: { id: id.toString() },
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: () => {
					// Invalidate admins list since admin was removed
					queryClient.invalidateQueries({ queryKey: ["admins"] });
					console.log("Admin removed successfully");
				},
				onError: () => console.log("Error: Failed to remove admin"),
			}),
		},

		// Notifications
		notifications: {
			getAll: () =>
				useQuery({
					queryKey: ["notifications"],
					queryFn: async () => {
						const result = await client.notifications.index.$get();
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
			getByUserId: (userId: string) =>
				useQuery({
					queryKey: ["notifications", "user", userId],
					queryFn: async () => {
						const result = await client.notifications.user[":userId"].$get({
							param: { userId },
						});
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
			create: useMutation({
				mutationFn: async (data: CreateNotificationInput) => {
					const result = await client.notifications.index.$post({ json: data });
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					// Invalidate notifications for the user who received it
					queryClient.invalidateQueries({ queryKey: ["notifications"] });
					queryClient.invalidateQueries({
						queryKey: ["notifications", "user", variables.userId],
					});
					console.log("Notification created successfully");
				},
				onError: () => console.log("Error: Failed to create notification"),
			}),
			markAsRead: useMutation({
				mutationFn: async (id: number) => {
					const result = await client.notifications[":id"].read.$put({
						param: { id: id.toString() },
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: () => {
					// Invalidate notifications since read status changed
					queryClient.invalidateQueries({ queryKey: ["notifications"] });
					console.log("Notification marked as read");
				},
				onError: () =>
					console.log("Error: Failed to mark notification as read"),
			}),
			delete: useMutation({
				mutationFn: async (id: number) => {
					const result = await client.notifications[":id"].$delete({
						param: { id: id.toString() },
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: () => {
					// Invalidate notifications since one was deleted
					queryClient.invalidateQueries({ queryKey: ["notifications"] });
					console.log("Notification deleted successfully");
				},
				onError: () => console.log("Error: Failed to delete notification"),
			}),
		},

		// Favourites
		favourites: {
			getAll: () =>
				useQuery({
					queryKey: ["favourites"],
					queryFn: async () => {
						const result = await client.favourites.index.$get();
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
			getByUserId: (userId: string) =>
				useQuery({
					queryKey: ["favourites", "user", userId],
					queryFn: async () => {
						const result = await client.favourites.user[":userId"].$get({
							param: { userId },
						});
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
			create: useMutation({
				mutationFn: async (data: CreateFavouriteInput) => {
					const result = await client.favourites.index.$post({ json: data });
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					// Invalidate user's favourites since new item was added
					queryClient.invalidateQueries({ queryKey: ["favourites"] });
					queryClient.invalidateQueries({
						queryKey: ["favourites", "user", variables.userId],
					});
					console.log("Item added to favourites");
				},
				onError: () => console.log("Error: Failed to add to favourites"),
			}),
			delete: useMutation({
				mutationFn: async (id: number) => {
					const result = await client.favourites[":id"].$delete({
						param: { id: id.toString() },
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: () => {
					// Invalidate favourites since item was removed
					queryClient.invalidateQueries({ queryKey: ["favourites"] });
					console.log("Item removed from favourites");
				},
				onError: () => console.log("Error: Failed to remove from favourites"),
			}),
			deleteByUserAndItem: useMutation({
				mutationFn: async ({
					userId,
					menuItemId,
				}: {
					userId: string;
					menuItemId: number;
				}) => {
					const result = await client.favourites.user[":userId"].item[
						":menuItemId"
					].$delete({ param: { userId, menuItemId: menuItemId.toString() } });
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					// Invalidate user's favourites since item was removed
					queryClient.invalidateQueries({ queryKey: ["favourites"] });
					queryClient.invalidateQueries({
						queryKey: ["favourites", "user", variables.userId],
					});
					console.log("Item removed from favourites");
				},
				onError: () => console.log("Error: Failed to remove from favourites"),
			}),
		},

		// Cart
		cart: {
			getByUserId: (userId: string) =>
				useQuery({
					queryKey: ["cart", userId],
					queryFn: async () => {
						const result = await client.cart.user[":userId"].$get({
							param: { userId },
						});
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
					enabled: !!userId,
				}),
			addItem: useMutation({
				mutationFn: async ({
					userId,
					item,
				}: {
					userId: string;
					item: AddToCartInput;
				}) => {
					const result = await client.cart.user[":userId"].$post({
						param: { userId },
						json: item,
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					queryClient.invalidateQueries({
						queryKey: ["cart", variables.userId],
					});
					console.log("Item added to cart");
				},
				onError: () => console.log("Error: Failed to add item to cart"),
			}),
			updateItem: useMutation({
				mutationFn: async ({
					cartItemId,
					quantity,
				}: {
					cartItemId: number;
					quantity: UpdateCartItemInput;
				}) => {
					const result = await client.cart[":id"].$put({
						param: { id: cartItemId.toString() },
						json: quantity,
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					queryClient.invalidateQueries({ queryKey: ["cart"] });
					console.log("Cart item updated");
				},
				onError: () => console.log("Error: Failed to update cart item"),
			}),
			removeItem: useMutation({
				mutationFn: async (cartItemId: number) => {
					const result = await client.cart[":id"].$delete({
						param: { id: cartItemId.toString() },
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: () => {
					// Invalidate all cart queries since we don't have userId in variables
					queryClient.invalidateQueries({ queryKey: ["cart"] });
					console.log("Item removed from cart");
				},
				onError: () => console.log("Error: Failed to remove item from cart"),
			}),
			clearCart: useMutation({
				mutationFn: async (userId: string) => {
					const result = await client.cart.user[":userId"].all.$delete({
						param: { userId },
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					queryClient.invalidateQueries({ queryKey: ["cart", variables] });
					console.log("Cart cleared");
				},
				onError: () => console.log("Error: Failed to clear cart"),
			}),
		},

		// Preferences
		preferences: {
			getByUserId: (userId: string) =>
				useQuery({
					queryKey: ["preferences", userId],
					queryFn: async () => {
						const result = await client.preferences.user[":userId"].$get({
							param: { userId },
						});
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
			create: useMutation({
				mutationFn: async ({
					userId,
					preferences,
				}: {
					userId: string;
					preferences: CreatePreferencesInput;
				}) => {
					const result = await client.preferences.user[":userId"].$post({
						param: { userId },
						json: preferences,
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: () => console.log("Preferences created successfully"),
				onError: () => console.log("Error: Failed to create preferences"),
			}),
			update: useMutation({
				mutationFn: async ({
					userId,
					preferences,
				}: {
					userId: string;
					preferences: UpdatePreferencesInput;
				}) => {
					const result = await client.preferences.user[":userId"].$put({
						param: { userId },
						json: preferences,
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: () => console.log("Preferences updated successfully"),
				onError: () => console.log("Error: Failed to update preferences"),
			}),
			reset: useMutation({
				mutationFn: async (userId: string) => {
					const result = await client.preferences.user[":userId"].$delete({
						param: { userId },
					});
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: () => console.log("Preferences reset to defaults"),
				onError: () => console.log("Error: Failed to reset preferences"),
			}),
		},

		// Analytics
		analytics: {
			getDashboard: () =>
				useQuery({
					queryKey: ["analytics", "dashboard"],
					queryFn: async () => {
						const result = await client.analytics.dashboard.$get();
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
			getOrderStats: () =>
				useQuery({
					queryKey: ["analytics", "orders"],
					queryFn: async () => {
						const result = await client.analytics.orders.stats.$get();
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
			getPopularItems: () =>
				useQuery({
					queryKey: ["analytics", "menu", "popular"],
					queryFn: async () => {
						const result = await client.analytics.menu.popular.$get();
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
			getCustomerStats: () =>
				useQuery({
					queryKey: ["analytics", "customers"],
					queryFn: async () => {
						const result = await client.analytics.customers.stats.$get();
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
				}),
		},

		// Uploads
		uploads: {
			uploadMenuImage: useMutation({
				mutationFn: async ({
					menuItemId,
					image,
				}: {
					menuItemId: number;
					image: File;
				}) => {
					const formData = new FormData();
					formData.append("image", image);
					const response = await fetch(
						`${baseUrl}/uploads/menu/${menuItemId}/image`,
						{
							method: "POST",
							body: formData,
						},
					);
					const parsed = await response.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					// Invalidate menu items since image was updated
					queryClient.invalidateQueries({ queryKey: ["menu", "items"] });
					queryClient.invalidateQueries({
						queryKey: ["menu", "items", variables.menuItemId],
					});
					console.log("Image uploaded successfully");
				},
				onError: () => console.log("Error: Failed to upload image"),
			}),
			deleteMenuImage: useMutation({
				mutationFn: async (menuItemId: number) => {
					const result = await client.uploads.menu[":menuItemId"].image.$delete(
						{ param: { menuItemId: menuItemId.toString() } },
					);
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					// Invalidate menu items since image was deleted
					queryClient.invalidateQueries({ queryKey: ["menu", "items"] });
					queryClient.invalidateQueries({
						queryKey: ["menu", "items", variables],
					});
					console.log("Image deleted successfully");
				},
				onError: () => console.log("Error: Failed to delete image"),
			}),
			uploadUserImage: useMutation({
				mutationFn: async ({
					userId,
					image,
				}: {
					userId: string;
					image: File;
				}) => {
					const formData = new FormData();
					formData.append("image", image);
					// Use fetch directly for FormData since Hono client expects JSON
					const response = await fetch(
						`${baseUrl}/uploads/user/${userId}/image`,
						{
							method: "POST",
							credentials: "include",
							body: formData,
						},
					);
					const parsed = await response.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					// Invalidate user profile since image was updated
					queryClient.invalidateQueries({
						queryKey: ["users", "profile", variables.userId],
					});
					console.log("Profile image uploaded successfully");
				},
				onError: () => console.log("Error: Failed to upload profile image"),
			}),
			deleteUserImage: useMutation({
				mutationFn: async (userId: string) => {
					const result = await fetch(
						`${baseUrl}/uploads/user/${userId}/image`,
						{
							method: "DELETE",
							// BetterAuth session in this app uses cookies.
							credentials: "include",
						},
					);
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onSuccess: (_, variables) => {
					// Invalidate user profile since image was deleted
					queryClient.invalidateQueries({
						queryKey: ["users", "profile", variables],
					});
					console.log("Profile image deleted successfully");
				},
				onError: () => console.log("Error: Failed to delete profile image"),
			}),
			getRecentImages: (params: {
				usageType: string;
				entityId: number;
				limit?: number;
				offset?: number;
			}) =>
				useQuery({
					queryKey: ["uploads", "recent", params],
					queryFn: async () => {
						const result = await client.uploads.recent.$get({
							query: {
								usageType: params.usageType as any,
								entityId: params.entityId.toString(),
								limit: params.limit,
								offset: params.offset,
							},
						});
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
					enabled: !!params.usageType && !!params.entityId,
				}),
		},

		// Payments
		payments: {
			createCheckout: useMutation({
				mutationFn: async (data: CreateCheckoutSessionInput) => {
					const result = await client.payments.checkout.$post({ json: data });
					const parsed = await result.json();
					if (!parsed.success) throw new Error(parsed.error);
					return parsed.data;
				},
				onError: () => console.log("Error: Failed to create checkout session"),
			}),
			getSession: (sessionId: string) =>
				useQuery({
					queryKey: ["payments", "session", sessionId],
					queryFn: async () => {
						const result = await client.payments.session[":sessionId"].$get({
							param: { sessionId },
						});
						const parsed = await result.json();
						if (!parsed.success) throw new Error(parsed.error);
						return parsed.data;
					},
					enabled: !!sessionId,
				}),
		},
	};
}
